/**
 * CampaignDetailDialog
 *
 * P_SURVEY_INTELLIGENCE_ENGINE_1.0 — Slice A
 *
 * Shows the five-facet "explain itself" block for a single Insight
 * Campaign and exposes a single CTA to launch the underlying Guided
 * Survey recipe.
 *
 * The five facets are the canonical schema for every customer-facing
 * artefact in the Intelligence Engine (PRD: Permanent product
 * principle).
 *   1. What       — plain-business definition
 *   2. Why        — why it matters
 *   3. How        — how to operate the campaign end-to-end
 *   4. What good  — benchmarks / target bands
 *   5. Actions    — concrete next steps
 *
 * This dialog is read-only — it never mutates a survey. Launching
 * goes through the existing Guided Survey wizard with the recipe id
 * preset.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, X } from 'lucide-react';

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { surveysAPI } from '../api';

const FACETS = [
    { key: 'what', icon: '📖', titleKey: 'surveys.campaigns.facet.what', defaultTitle: 'What this is' },
    { key: 'why', icon: '🎯', titleKey: 'surveys.campaigns.facet.why', defaultTitle: 'Why it matters' },
    { key: 'how', icon: '🛠️', titleKey: 'surveys.campaigns.facet.how', defaultTitle: 'How to operate it' },
    { key: 'good', icon: '📊', titleKey: 'surveys.campaigns.facet.good', defaultTitle: 'What good looks like' },
    { key: 'actions', icon: '✅', titleKey: 'surveys.campaigns.facet.actions', defaultTitle: 'Recommended actions' },
];

const CampaignDetailDialog = ({ open, onOpenChange, campaignId, onLaunch }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'en';
    const tr = (bag) => (bag && (bag[lang] || bag.en)) || '';

    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open || !campaignId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setDetail(null);
        surveysAPI.campaigns.get(campaignId)
            .then((d) => { if (!cancelled) setDetail(d); })
            .catch((err) => {
                if (!cancelled) {
                    setError(err?.response?.data?.detail || t('surveys.campaigns.loadFailed', 'Could not load this campaign.'));
                }
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, campaignId, t]);

    const handleLaunch = () => {
        if (!detail) return;
        onLaunch?.(detail.recipe_id);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl max-h-[88vh] overflow-y-auto"
                data-testid="campaign-detail-dialog"
                aria-describedby={undefined}
            >
                <DialogHeader>
                    {detail && (
                        <div className="flex items-center gap-2 text-fuchsia-600 dark:text-fuchsia-300 text-xs font-semibold uppercase tracking-wide">
                            <Sparkles className="w-3.5 h-3.5" />
                            <span>{t('surveys.campaigns.eyebrow', 'Insight Campaign')}</span>
                        </div>
                    )}
                    <DialogTitle className="text-lg flex items-center gap-2" data-testid="campaign-detail-title">
                        {detail && (
                            <>
                                <span className="text-2xl" aria-hidden="true">{detail.icon}</span>
                                <span>{tr(detail.title)}</span>
                            </>
                        )}
                        {loading && t('common.loading', 'Loading…')}
                    </DialogTitle>
                </DialogHeader>

                {error && (
                    <p
                        className="text-sm text-rose-600 dark:text-rose-300"
                        data-testid="campaign-detail-error"
                    >
                        {error}
                    </p>
                )}

                {detail && (
                    <>
                        <p
                            className="text-sm text-slate-700 dark:text-slate-200"
                            data-testid="campaign-detail-tagline"
                        >
                            {tr(detail.tagline)}
                        </p>

                        <div className="space-y-3 mt-1">
                            {FACETS.map((f) => (
                                <div
                                    key={f.key}
                                    className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/40 p-3"
                                    data-testid={`campaign-facet-${f.key}`}
                                >
                                    <div className="flex items-center gap-2 text-xs font-semibold text-slate-800 dark:text-slate-100 uppercase tracking-wide">
                                        <span aria-hidden="true">{f.icon}</span>
                                        <span>{t(f.titleKey, f.defaultTitle)}</span>
                                    </div>
                                    <p className="text-sm text-slate-700 dark:text-slate-200 mt-1.5 leading-relaxed">
                                        {tr(detail.facets?.[f.key])}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Preview of the survey questions */}
                        {Array.isArray(detail.recipe?.preview_questions) && detail.recipe.preview_questions.length > 0 && (
                            <div className="mt-2">
                                <div className="text-xs font-semibold text-slate-800 dark:text-slate-100 uppercase tracking-wide mb-1.5">
                                    {t('surveys.campaigns.previewHeading', "What guests will see")}
                                </div>
                                <ul
                                    className="text-sm text-slate-700 dark:text-slate-200 space-y-1 list-disc pl-5"
                                    data-testid="campaign-detail-preview-list"
                                >
                                    {detail.recipe.preview_questions.map((q, i) => (
                                        <li key={q.id || i}>
                                            {(q.text && (q.text[lang] || q.text.en)) || `Q${i + 1}`}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="flex items-center justify-between gap-2 pt-3 mt-2 border-t border-slate-200 dark:border-slate-700">
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => onOpenChange?.(false)}
                                data-testid="campaign-detail-close"
                            >
                                <X className="w-3.5 h-3.5 mr-1" />
                                {t('common.close', 'Close')}
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                onClick={handleLaunch}
                                data-testid="campaign-detail-launch-btn"
                            >
                                <Sparkles className="w-3.5 h-3.5 mr-1" />
                                {t('surveys.campaigns.launchCta', 'Launch this initiative')}
                            </Button>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default CampaignDetailDialog;
